import { string } from 'yup';

import { FormErrorMessage } from '~/constants/messages';
import { passwordRegexp } from '~/constants/regexp';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 72;

export type PasswordPolicy = {
  minimumLength: number;
  alphanumericRequired: boolean;
};

// ポリシーを取得前の既定値
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minimumLength: PASSWORD_MIN_LENGTH,
  alphanumericRequired: false,
};

// 法人サインアップ用。法人がまだ無く最小文字数を引けないが、英数字混在は全社共通ルールのため必須にする
export const SIGNUP_PASSWORD_POLICY: PasswordPolicy = {
  minimumLength: PASSWORD_MIN_LENGTH,
  alphanumericRequired: true,
};

export const passwordPlaceholder = (policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY) =>
  `半角英数記号${policy.minimumLength}文字以上`;

// GET /api/organization_names/:slug のレスポンス（ログイン導線はGraphQLを叩けない）
// axios-case-converter がレスポンスのキーを camelCase に変換するため snake_case では受け取らない
export type PasswordPolicyResponse = PasswordPolicy;

export const toPasswordPolicy = (response?: PasswordPolicyResponse): PasswordPolicy =>
  response ?? DEFAULT_PASSWORD_POLICY;

const isBlank = (password: string) => password.length === 0;

const containsAlphanumeric = (password: string) =>
  /[A-Za-z]/.test(password) && /[0-9]/.test(password);

const minLengthMessage = (policy: PasswordPolicy) =>
  `${passwordPlaceholder(policy)}で入力してください`;
const maxLengthMessage = `${PASSWORD_MAX_LENGTH}文字以内で入力してください`;

// 入力が必須の画面向け
export const passwordSchema = (policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY) => {
  const schema = string()
    .label('パスワード')
    .min(policy.minimumLength, minLengthMessage(policy))
    .max(PASSWORD_MAX_LENGTH, FormErrorMessage.maxLengthRequired)
    .matches(passwordRegexp, FormErrorMessage.alphaSymbol);

  if (!policy.alphanumericRequired) return schema;

  return schema.test('alphanumeric', FormErrorMessage.alphanumeric, (password) =>
    containsAlphanumeric(password ?? ''),
  );
};

// 変更しないときは空で送る画面向け（未入力なら検証しない）
export const optionalPasswordSchema = (policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY) =>
  string()
    .label('パスワード')
    .default('')
    .test('password', FormErrorMessage.alphaSymbol, (password) =>
      isBlank(password) ? true : passwordRegexp.test(password),
    )
    .test('password', minLengthMessage(policy), (password) =>
      isBlank(password) ? true : password.length >= policy.minimumLength,
    )
    .test('password', maxLengthMessage, (password) =>
      isBlank(password) ? true : password.length <= PASSWORD_MAX_LENGTH,
    )
    .test('password', FormErrorMessage.alphanumeric, (password) =>
      isBlank(password) || !policy.alphanumericRequired ? true : containsAlphanumeric(password),
    );

// CSV 一括登録の行検証向け
export const isValidPassword = (
  password: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY,
) =>
  passwordRegexp.test(password) &&
  password.length >= policy.minimumLength &&
  password.length <= PASSWORD_MAX_LENGTH &&
  (!policy.alphanumericRequired || containsAlphanumeric(password));

export type PasswordRule = {
  label: string;
  satisfied: boolean;
};

// 入力欄の下に出すルールのチェックリスト（満たしていない条件が分かるようにする）
export const passwordRules = (password: string, policy: PasswordPolicy): PasswordRule[] => {
  const rules: PasswordRule[] = [
    {
      label: `${policy.minimumLength}文字以上${PASSWORD_MAX_LENGTH}文字以内`,
      satisfied: password.length >= policy.minimumLength && password.length <= PASSWORD_MAX_LENGTH,
    },
  ];

  if (policy.alphanumericRequired) {
    rules.push({ label: '英字と数字を組み合わせる', satisfied: containsAlphanumeric(password) });
  }

  rules.push({ label: '使用できる文字は半角英数記号', satisfied: passwordRegexp.test(password) });

  return rules;
};
